'use client'

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Briefcase, MapPin, Clock, DollarSign, Users, Award, Heart, Zap, Shield, Coffee, Laptop, Plane, Calendar } from 'lucide-react'
import Link from 'next/link'
import DynamicCompanyInfo from '@/components/DynamicCompanyInfo'

export default function CareerPage() {
  const [dbJobs, setDbJobs] = useState<any[]>([])
  const [loadingJobs, setLoadingJobs] = useState(true)

  useEffect(() => {
    fetch('/api/recruitment/public-jobs')
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setDbJobs(data) })
      .catch(() => {})
      .finally(() => setLoadingJobs(false))
  }, [])

  const benefits = [
    {
      icon: <Heart className="w-8 h-8" />,
      title: 'Health & Wellness',
      description: 'Comprehensive health insurance, mental health support, and wellness programs.'
    },
    {
      icon: <Laptop className="w-8 h-8" />,
      title: 'Remote Work',
      description: 'Flexible remote work options with modern equipment and workspace allowance.'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Learning & Development',
      description: 'Professional development budget, conference attendance, and certification support.'
    },
    {
      icon: <Coffee className="w-8 h-8" />,
      title: 'Work-Life Balance',
      description: 'Flexible hours, unlimited PTO, and team building activities.'
    },
    {
      icon: <DollarSign className="w-8 h-8" />,
      title: 'Competitive Salary',
      description: 'Market-competitive compensation with performance bonuses and equity options.'
    },
    {
      icon: <Plane className="w-8 h-8" />,
      title: 'Travel Opportunities',
      description: 'International assignments and conference travel opportunities.'
    }
  ]

  const companyValues = [
    {
      icon: <Zap className="w-6 h-6" />,
      title: 'Innovation First',
      description: 'We encourage creative thinking and embrace new technologies.'
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Team Collaboration',
      description: 'We believe in the power of diverse teams working together.'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Quality Excellence', 
      description: 'We maintain the highest standards in everything we do.'
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: 'Client Success',
      description: 'Our clients\' success is the measure of our own success.'
    }
  ]

  const stats = [
    { value: '40+', label: 'Team Members' },
    { value: '4.8★', label: 'Employee Rating' },
    { value: '15+', label: 'Countries' },
    { value: '95%', label: 'Retention Rate' }
  ]

  return (
    <main className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 via-white to-blue-50">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Build Your <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">Dream Career</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8">
              Join a team of passionate innovators creating cutting-edge technology solutions. 
              Grow your skills, make an impact, and shape the future with us.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <button className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg transition-all duration-300">
                View Open Positions
              </button>
              <button className="border-2 border-purple-600 text-purple-600 px-8 py-4 rounded-xl font-semibold hover:bg-purple-600 hover:text-white transition-all duration-300">
                Submit Resume
              </button>
            </div>
          </motion.div>

          {/* Company Stats */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 max-w-4xl mx-auto"
          >
            {stats.map((stat, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="text-3xl font-bold text-purple-600 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Open Positions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore exciting opportunities to grow your career with cutting-edge projects and technologies.
            </p>
          </motion.div>

          {/* Dynamic Jobs from Database */}
          {loadingJobs ? (
            <div className="text-center py-8"><div className="animate-pulse h-20 bg-gray-100 rounded-2xl max-w-3xl mx-auto"></div></div>
          ) : dbJobs.length > 0 ? (
            <div className="space-y-6">
              {dbJobs.map((job: any, index: number) => (
                <motion.div
                  key={job._id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-gradient-to-r from-gray-50 to-purple-50 rounded-2xl p-8 hover:shadow-lg transition-all duration-300"
                >
                  <div className="grid lg:grid-cols-3 gap-8 items-center">
                    <div className="lg:col-span-2">
                      <div className="flex flex-wrap items-center gap-4 mb-4">
                        <h3 className="text-2xl font-bold text-gray-900">{job.title}</h3>
                        <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                          {job.department?.name || 'General'}
                        </span>
                      </div>
                      {job.description && (
                        <div className="text-gray-600 mb-4 line-clamp-2" dangerouslySetInnerHTML={{ __html: job.description.replace(/<[^>]*>/g, '').substring(0, 200) }} />
                      )}
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div className="flex items-center text-gray-600">
                          <Briefcase className="w-4 h-4 mr-2" />
                          {job.designation?.name || 'Multiple Roles'}
                        </div>
                        <div className="flex items-center text-gray-600">
                          <Users className="w-4 h-4 mr-2" />
                          {job.vacancies} {job.vacancies === 1 ? 'vacancy' : 'vacancies'}
                        </div>
                        <div className="flex items-center text-gray-600">
                          <Clock className="w-4 h-4 mr-2" />
                          {job.applicationMethod} • {job.interviewType}
                        </div>
                        <div className="flex items-center text-gray-600">
                          <Calendar className="w-4 h-4 mr-2" />
                          Opening: {new Date(job.openingDate).toLocaleDateString()}
                        </div>
                        <div className="flex items-center text-gray-600">
                          <Calendar className="w-4 h-4 mr-2" />
                          Closing: {new Date(job.closingDate).toLocaleDateString()}
                        </div>
                      </div>
                      {job.requiredDocuments?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {job.requiredDocuments.slice(0, 4).map((doc: string, i: number) => (
                            <span key={i} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">{doc}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="text-center lg:text-right">
                      <Link 
                        href={`/about/career/apply/${job._id}`}
                        className="inline-block bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300"
                      >
                        Apply Now
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-5xl mb-4">🔍</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Open Positions Right Now</h3>
              <p className="text-gray-500">Check back later or submit your resume and we&apos;ll reach out when a suitable role opens up.</p>
            </div>
          )}
        </div>
      </section>

      {/* Benefits & Perks */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Benefits & Perks</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We invest in our team members' success with comprehensive benefits and a supportive work environment.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl flex items-center justify-center text-white mb-6">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Culture */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Company Culture</h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                <DynamicCompanyInfo type="companyName">
                  {(companyInfo) => (
                    `At ${companyInfo?.companyName || 'our company'}, we foster an inclusive, innovative, and collaborative environment 
                    where every team member can thrive and make meaningful contributions to groundbreaking projects.`
                  )}
                </DynamicCompanyInfo>
              </p>
              
              <div className="space-y-6">
                {companyValues.map((value, index) => (
                  <div key={index} className="flex items-start">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                      <div className="text-purple-600">{value.icon}</div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{value.title}</h3>
                      <p className="text-gray-600">{value.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Illustration */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-3xl p-12">
                <div className="text-center">
                  <div className="text-6xl mb-6">🚀</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Join Our Mission</h3>
                  <p className="text-gray-600 mb-6">
                    Be part of a team that's shaping the future of technology and making a real impact on businesses worldwide.
                  </p>
                  <div className="flex justify-center space-x-4 text-4xl">
                    <span>👩‍💻</span>
                    <span>👨‍💻</span>
                    <span>🌟</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-purple-600 to-blue-600">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">Ready to Start Your Journey?</h2>
            <p className="text-xl text-purple-100 mb-8 max-w-3xl mx-auto">
              Don't see the perfect role? We're always looking for exceptional talent. 
              Send us your resume and let's explore opportunities together.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link href="/contact" className="bg-white text-purple-600 px-8 py-4 rounded-xl font-semibold hover:shadow-xl transition-all duration-300">
                Get In Touch
              </Link>
              <button className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-purple-600 transition-all duration-300">
                Submit Resume
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  )
}